import { useCallback } from 'react';
import { useAppDispatch } from '../../../../redux/hooks';
import { toggleChartSettingSortAction } from '../../../../redux/slices/analyses';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../../utils/types';
import KubitTooltip from '../../../KubitTooltip';
import { useChartContext } from '../../ChartContext';
import { RetentionTableType } from '../../interfaces';
import getEventNameListFromEventConditions from '../utils/getEventNameListFromEventConditions';
import { RetentionTableHeaderSort } from './RetentionTableHeader';
import * as RetentionTableUI from './RetentionTableUI';
import { CellAlignment } from './RetentionTableUI';
import { GridCellRenderer } from 'react-virtualized';
import { styleWithBorder, topRowCell } from './borders';

export default (analysisId: string) => {
  const dispatch = useAppDispatch();

  const {
    chartData,
    settings: { chartSettings },
    metadata: { query },
  } = useChartContext();
  assertCondition(chartData.queryType === QueryType.Retention);
  assertCondition(query?.__typename === 'RetentionQuery');
  const { sort } = chartSettings;
  const isOverTime = chartData.table.type === RetentionTableType.OverTime;

  const {
    startConditions,
    returnConditions,
    cohortUnit,
    view,
    retentionUnit,
    subject,
  } = query;

  const startingEventsParsed =
    getEventNameListFromEventConditions(startConditions);

  const returningEventsParsed =
    getEventNameListFromEventConditions(returnConditions);

  const retentionHeaderRenderer = useCallback<GridCellRenderer>(
    ({ columnIndex, key, style }) => {
      if (columnIndex === 0) {
        const text = isOverTime
          ? `Retention (${retentionUnit.substring(0, 1)})`
          : `Cohort Date (${cohortUnit.substring(0, 1)})`;
        return (
          <RetentionTableUI.ColumnHeaderCell
            key={key}
            style={styleWithBorder(style, topRowCell(columnIndex))}
            $isBackgroundFilled
            $isClickable
            $alignment={CellAlignment.Left}
            onClick={() =>
              dispatch(toggleChartSettingSortAction({ analysisId }))
            }
          >
            <RetentionTableHeaderSort sort={sort} text={text} />
          </RetentionTableUI.ColumnHeaderCell>
        );
      }

      if (columnIndex === 1) {
        if (isOverTime) {
          return (
            <RetentionTableUI.ColumnHeaderCell
              key={key}
              style={styleWithBorder(style, topRowCell(columnIndex))}
              $isBackgroundFilled={true}
              $alignment={CellAlignment.Left}
              title={'Cohort Dates'}
            >
              {'Cohort Dates'}
            </RetentionTableUI.ColumnHeaderCell>
          );
        }

        const subjectLabel = `Subjects (${subject?.name})`;

        return (
          <RetentionTableUI.ColumnHeaderCell
            key={key}
            style={styleWithBorder(style, topRowCell(columnIndex))}
            $isBackgroundFilled={true}
            title={subjectLabel}
            $alignment={CellAlignment.Left}
          >
            <div>
              <RetentionTableUI.SubjectLabel>
                {subjectLabel}
              </RetentionTableUI.SubjectLabel>
              <KubitTooltip
                placement="bottom-start"
                content={startingEventsParsed}
                noOpenDelay={true}
              >
                <RetentionTableUI.EventInfo>
                  {startingEventsParsed}
                </RetentionTableUI.EventInfo>
              </KubitTooltip>
            </div>
          </RetentionTableUI.ColumnHeaderCell>
        );
      }

      if (columnIndex === 2 && !isOverTime) {
        return (
          <RetentionTableUI.ColumnHeaderCell
            key={key}
            style={styleWithBorder(style, topRowCell(columnIndex))}
            $isBackgroundFilled={true}
            title={view.name}
            $alignment={CellAlignment.Left}
          >
            <div>
              {view.name}
              <KubitTooltip
                placement="bottom-start"
                content={returningEventsParsed}
                noOpenDelay={true}
              >
                <RetentionTableUI.EventInfo>
                  {returningEventsParsed}
                </RetentionTableUI.EventInfo>
              </KubitTooltip>
            </div>
          </RetentionTableUI.ColumnHeaderCell>
        );
      }
    },
    [
      cohortUnit,
      view,
      isOverTime,
      retentionUnit,
      returningEventsParsed,
      sort,
      startingEventsParsed,
      analysisId,
      dispatch,
      subject?.name,
    ],
  );

  const cellRenderer = useCallback<GridCellRenderer>(
    ({ rowIndex, ...rest }) => {
      if (rowIndex === 0) {
        return retentionHeaderRenderer({ rowIndex, ...rest });
      }
    },
    [retentionHeaderRenderer],
  );

  return { cellRenderer };
};
